import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { useTelephoneContext } from "@/modules/lead-page/new/hooks/telephone-context.hook";
import useTelephoneConfig from "@/modules/lead-page/new/hooks/telephone-config.hook";
import useTelephoneMenuActions from "@/modules/lead-page/new/hooks/telephone-menu-action.hook";
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import { MoreVerticalIcon } from "lucide-react";
import useLeadContactMethods from "@/modules/lead-page/hooks/lead-info/lead-contact-methods.hook";
import CallIcon from "@/assets/icons/lead-page/call-icon";
import WhatsappIcon from "@/assets/icons/lead-page/whatsapp-icon";
import { handleContactWhatsapp } from "@/modules/common/utils";
import CallPopoverButton from "@/modules/common/components/call-popover-button";
import { normalizeContactPhone } from "@/modules/common/utils/customer-contact";
import CustomerContactDisplay from "@/modules/common/components/customer-contact";

const OtherNumbers = () => {
  const theme = useTheme();

  const { telephone } = useTelephoneContext();
  const { otherNumbers } = useTelephoneConfig();
  const { handleSearchTruecaller, isLoading } = useLeadContactMethods();

  const {
    handleMenuClick,
    openMenu,
    currentTelephoneForMenu,
    handleMenuClose,
    anchorEl,
  } = useTelephoneMenuActions();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.sm.regular,
          color: theme.nawy.colors.semantic.text.secondary,
        }}
      >
        Other numbers
      </Typography>

      {otherNumbers?.map((otherTelephone) => {
        const isDeleting =
          telephone.deleteTelephone.isDeletingTelephone &&
          telephone.deleteTelephone.telephoneIdToDelete === otherTelephone.uid;

        return (
          <Box
            key={otherTelephone.id}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  ...theme.nawy.typography.english.variants.body.md.regular,
                }}
              >
                <CustomerContactDisplay contacts={[otherTelephone]} actions="copy" />
              </Box>

              <Box sx={{ display: "flex", gap: "6px" }}>
                <CallPopoverButton phoneNumber={otherTelephone.telephone}>
                  <IconButton
                    sx={{
                      width: 36,
                      height: 36,
                      opacity: isLoading ? 0.6 : 1,
                    }}
                    disabled={isLoading}
                  >
                    <CallIcon fill={theme.nawy.colors.semantic.text.accent} />
                  </IconButton>
                </CallPopoverButton>

                <IconButton
                  disabled={!normalizeContactPhone(otherTelephone.telephone)}
                  sx={{ width: 36, height: 36 }}
                  onClick={() =>
                    handleContactWhatsapp(otherTelephone.telephone)
                  }
                >
                  <WhatsappIcon />
                </IconButton>
              </Box>
            </Box>

            {isDeleting ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              <CommonIconButton
                variant="ghost"
                size="md"
                aria-label="more"
                id={`long-button-${otherTelephone.id}`}
                aria-controls={openMenu ? "long-menu" : undefined}
                aria-expanded={openMenu ? "true" : undefined}
                aria-haspopup="true"
                onClick={(event) => handleMenuClick(event, otherTelephone)}
              >
                <MoreVerticalIcon />
              </CommonIconButton>
            )}
          </Box>
        );
      })}

      {currentTelephoneForMenu && (
        <Menu
          id="long-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          slotProps={{
            paper: {
              style: {
                maxHeight: 48 * 4.5,
                width: "20ch",
              },
            },
          }}
        >
          <MenuItem
            disabled={!normalizeContactPhone(currentTelephoneForMenu.telephone)}
            onClick={() =>
              handleSearchTruecaller(currentTelephoneForMenu.telephone)
            }
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.md.regular,
              }}
            >
              Truecaller
            </Typography>
          </MenuItem>

          {!currentTelephoneForMenu.primary &&
            currentTelephoneForMenu.deletedAt === null && (
              <MenuItem
                onClick={() => {
                  telephone.makeTelephonePrimary.setTelephoneIdToMakeDefault(
                    currentTelephoneForMenu.uid
                  );
                  telephone.makeTelephonePrimary.handleMakeTelephonePrimary(
                    currentTelephoneForMenu.uid
                  );
                  handleMenuClose();
                }}
                disabled={
                  telephone.makeTelephonePrimary.isMakingTelephonePrimary &&
                  telephone.makeTelephonePrimary.telephoneIdToMakeDefault ===
                    currentTelephoneForMenu.uid
                }
              >
                {telephone.makeTelephonePrimary.isMakingTelephonePrimary &&
                telephone.makeTelephonePrimary.telephoneIdToMakeDefault ===
                  currentTelephoneForMenu.uid ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <Typography
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md.regular,
                    }}
                  >
                    Set default
                  </Typography>
                )}
              </MenuItem>
            )}

          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.DELETE_CUSTOMER_TELEPHONE,
                level: PermissionLevel.WRITE,
              },
            ]}
          >
            <MenuItem
              onClick={() => {
                telephone.deleteTelephone.setTelephoneIdToDelete(
                  currentTelephoneForMenu.uid
                );
                telephone.deleteTelephone.handleDeleteTelephone(
                  currentTelephoneForMenu.uid
                );
                handleMenuClose();
              }}
              disabled={currentTelephoneForMenu.deletedAt !== null}
              sx={{
                color:
                  currentTelephoneForMenu.deletedAt !== null
                    ? theme.nawy.colors.semantic.text.tertiary
                    : "inherit",
              }}
            >
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color:
                    currentTelephoneForMenu.deletedAt !== null
                      ? "inherit"
                      : theme.nawy.colors.semantic.text.extension.negative,
                }}
              >
                {currentTelephoneForMenu.deletedAt !== null
                  ? "Already deleted"
                  : "Delete"}
              </Typography>
            </MenuItem>
          </PermissionGuard>
        </Menu>
      )}
    </Box>
  );
};

export default OtherNumbers;
